/**
 * @module BubbleConferences
 */

import { Contact } from 'rainbow-web-sdk/models/contact.model';
import { User } from './user.model';
import { JingleSession } from 'rainbow-web-sdk/libs/strophe/strophe.jingle.session';

/**
 * This class is used to represent a web conference participant object that is part of the WebConference
 */
export class BubbleConferenceParticipant {
    /**
     * The ID of the participant;
     * @readonly
     */
    public id: string;

    /**
     * Contact object related to the web conference participant
     * @readonly
     * @internal
     */
    public contact: Contact;

    public get user(): User { return this.contact; }

    /**
     * Whether the participant is currently publishing his video in the conference
     * @readonly
     */
    public get isVideoAvailable(): boolean {
        return this._isVideoAvailable;
    }

    public set isVideoAvailable(value: boolean) {
        this._isVideoAvailable = value;
        if (!value) {
            //in case we remove the video, reset the restriciton streams
            this.quailityLimitationSubstreamLevel = 2;
            this.videoElementSizeSubstreamLevel = 2;
        }
    }

    private _isVideoAvailable: boolean;

    /**
     * The current video session related to that participant. If the session is null but the 'isVideoAvailable'
     * is true, then we can subscribe to this participant video.
     * @readonly
     */
    public videoSession: JingleSession;

    /**
     * Whether the participant is currently muted
     * @readonly
     */
    public mute: boolean;

    /**
     * Whether the participant has joined without microphone
     * @readonly
     */
    public noMicrophone: boolean;

    /**
     * The current role of the participant. It could be either a moderator, or participant
     * @readonly
     */
    public role: string;

    /**
     * Whether the current participant object is a sharing participant (in this case, the videoSession object is related to the sharing session)
     * @readonly
     */
    public isSharingParticipant: boolean;

    /**
     * Indicates whether the conference can be delegated to the current participant
     * @readonly
     */
    public delegateCapability: boolean;

    /**
     * Indicates whether the current publisher offers simulcast; This means that when subscribing (or later)
     * we can ask a different level substream (by default, there're 3 levels - low, medium and high)
     * @readonly
     */
    public simulcast: boolean;

    /**
     * Indicates the current sub stream level subscribed for this user. Only available for subscriptions to other videos;
     * Sub-stream level (0 - low, 1 - medium, 2 - high). Only available if simulcast is TRUE
     * @readonly
     */
    public subStreamLevel: number;

    /**
     * Indicates the max sub stream level based on the quality limitation that should be used.
     * Sub-stream level (0 - low, 1 - medium, 2 - high). Only available if simulcast is TRUE
     * @readonly
     */
    public quailityLimitationSubstreamLevel: number;

    /**
     * Indicates the current sub stream level based on the video element size that should be used
     * Sub-stream level (0 - low, 1 - medium, 2 - high). Only available if simulcast is TRUE
     * @readonly
     */
    public videoElementSizeSubstreamLevel: number;

    /**
     * Indicates the current temporal level (onky 2 values are available, 0 and 2)
     * @readonly
     */
    public temporalLevel: number;

    public subscriptionInProgress: boolean;

    /**
     * Indicates the current resourceId, if the user is the remote sharing participant.
     * @readonly
     */
    public resourceId: string;

    /**
     * If this is an external PSTN participant
     * @readonly
     */
    public isPstnParticipant: boolean;

    /**
     * The current state of the PSTN participant; Could be one of "in_progress", "ringing, "active", "hangup"
     * @readonly
     */
    public currentPstnState: string = "";

    /**
     * If the user is "pinned" (managed for UI part of the grid)
     * @readonly
     */
    public isPinned: boolean = false;

    /**
     * Indicates if the user has their hand raised
     * @readonly
     */
    public raiseHand: boolean = false;

    /**
     * Indicates the time when the user has raised their hand
     * @readonly
     */
    public raiseHandDate: Date = null;

    public static create(id: string) { return new BubbleConferenceParticipant(id); }

    constructor(id: string, resourceId = "") {
        this.id = id;
        this.subscriptionInProgress = false;
        this.mute = false;
        this.noMicrophone = false;
        this.isVideoAvailable = false;
        this.isSharingParticipant = false;
        this.delegateCapability = false;
        this.resourceId = resourceId;
        //add default values for quality and size restrictions
        this.quailityLimitationSubstreamLevel = 2;
        this.videoElementSizeSubstreamLevel = 2;
    }
}
